import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { fetchAnswerAudio, useAttemptReview } from "@/api/tests-api";
import { DictationPlayer } from "@/components/tests/DictationPlayer";

/**
 * The candidate's recordings, one case at a time.
 *
 * A LIST BECAME A WALK, deliberately. The review drawer stacked every
 * case on one scroll, which is fine for two and wrong for twenty: a
 * reviewer listening to dictation is doing one thing at a time, and the
 * screen should be doing one thing at a time with them. "Step 2 of 5" is
 * also the only honest answer to "how much of this is left", which a
 * stacked list never tells you.
 *
 * PRACTICE IS NOT HERE, and not because this filters it out -- the
 * server does. ``/attempts/{id}/review`` selects
 * ``TestAssignmentCase.phase == LIVE``, so a rehearsal has never been
 * reviewable and cannot become so by an accident on this screen. Worth
 * knowing before anyone "adds the missing cases".
 *
 * The step index is LOCAL state, not a route. Closing the console and
 * reopening starts at case one, which is the right default for a
 * reviewer coming back to a candidate rather than resuming a form.
 */
export function RecordingsStepper({
  attemptId,
  onOpenCase,
}: {
  attemptId: string;
  /** THE CONSOLE OWNS THE FULL-SCREEN VIEWER, not this. The case rail
   *  opens it too, and two components each mounting their own would be
   *  two viewers with two idea of which case is open. */
  onOpenCase: (index: number) => void;
}) {
  const review = useAttemptReview(attemptId);
  const [step, setStep] = useState(0);

  if (review.isLoading) {
    return (
      <p className="text-sm text-foreground-muted">Loading the recordings…</p>
    );
  }
  if (review.isError || !review.data) {
    return (
      <p className="text-sm text-status-urgent">
        Could not load this sitting&rsquo;s recordings.
      </p>
    );
  }

  const answers = review.data.answers;
  if (answers.length === 0) {
    return (
      <p data-cy="stepper-empty" className="text-sm text-foreground-muted">
        This sitting has no cases to review.
      </p>
    );
  }

  // Clamped rather than trusted: `answers` can shrink between renders
  // (a purge, a refetch), and an index past the end would blank the
  // panel with no explanation.
  const index = Math.min(step, answers.length - 1);
  const a = answers[index]!;
  const recorded = answers.filter((x) => x.has_audio).length;

  return (
    <section
      data-cy="recordings-stepper"
      className="rounded-xl border border-border bg-background-card p-4"
    >
      {/* The step counter sits at the TOP, above the case, because it is
          the thing a reviewer looks for on arriving and again after
          every Next. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3
            data-cy="stepper-position"
            className="text-sm font-semibold text-foreground"
          >
            Case {index + 1} of {answers.length}
          </h3>
          <p className="mt-0.5 text-xs text-foreground-muted">
            {recorded} of {answers.length} recorded
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-cy="stepper-prev"
            disabled={index === 0}
            onClick={() => setStep(index - 1)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
            Previous
          </button>
          <button
            type="button"
            data-cy="stepper-next"
            disabled={index >= answers.length - 1}
            onClick={() => setStep(index + 1)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
            <ChevronRight aria-hidden className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* One dot per case: position at a glance, and a filled dot means
          there is something to listen to. A reviewer can see from here
          that case 4 is the silent one. */}
      <ol className="mt-3 flex flex-wrap gap-1.5" data-cy="stepper-dots">
        {answers.map((x, i) => (
          <li key={x.assignment_case_id}>
            <button
              type="button"
              data-cy={`stepper-dot-${i}`}
              onClick={() => setStep(i)}
              aria-label={`Case ${i + 1}${x.has_audio ? "" : " (no recording)"}`}
              aria-current={i === index}
              title={x.title}
              className={[
                "h-2.5 w-2.5 rounded-full border transition-colors",
                i === index
                  ? "border-accent bg-accent"
                  : x.has_audio
                    ? "border-border-strong bg-foreground-muted"
                    : "border-border-strong bg-transparent",
              ].join(" ")}
            />
          </li>
        ))}
      </ol>

      <div className="mt-4 border-t border-border pt-3">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="text-sm font-medium text-foreground">
            {a.position + 1}. {a.title}
          </h4>
          <span className="shrink-0 text-xs text-foreground-muted">
            {a.recorded_at ? "Recorded" : "No recording"}
            {a.late && (
              <span
                data-cy="answer-late"
                title="Recorded after the deadline"
                className="ml-2 rounded-full bg-status-wip-bg px-2 py-0.5 text-[10px] font-semibold uppercase text-status-wip"
              >
                Late
              </span>
            )}
          </span>
        </div>

        {a.has_audio && a.answer_id ? (
          <div className="mt-2">
            {/* Keyed on the answer so stepping between cases REMOUNTS the
                player. It caches the object URL it fetched, which is
                right for scrubbing one recording and wrong here -- the
                audio behind the control changes with every Next, and a
                cached URL would play the previous candidate case. */}
            <DictationPlayer
              key={a.answer_id}
              load={() => fetchAnswerAudio(a.answer_id as string)}
              durationHint={a.duration_seconds}
            />
          </div>
        ) : (
          <p className="mt-2 text-sm italic text-foreground-muted">
            The candidate recorded nothing for this case.
          </p>
        )}

        {/* THE IMAGES TAKE THE SCREEN, they do not share this one.
            They were first shown here, in a collapsible panel beside
            the player -- inside a 1240px reading column, so roughly
            600px wide. That is not a viewport you can read a CT in: the
            stack scaled down to thumbnails and cut off at the edges. A
            radiologist cannot score what they cannot see.

            So this opens the case full-screen in the reading room, with
            the dictation, the case list and the note in its first panel
            tab -- see CaseReviewer. */}
        <button
          type="button"
          data-cy="btn-open-case-reviewer"
          className="sk-btn sk-sm mt-3"
          onClick={() => onOpenCase(step)}
        >
          Open the images for this case
        </button>

        {a.reviews.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1 border-t border-border pt-2">
            {a.reviews.map((r) => (
              <li key={r.id} className="text-xs text-foreground-muted">
                <span className="font-medium text-foreground">
                  {r.reviewer_name ?? "A reviewer"}:
                </span>{" "}
                {r.comments}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ON PAPER, EVERY CASE. The walk above shows one case at a time,
          which is right on screen and wrong in a record -- printing it
          would carry whichever case happened to be open. The reviewer's
          note is the part that has to survive, so the printed sheet
          lists all of them. */}
      <div className="sk-print-only mt-4 border-t border-border pt-3">
        <h4 className="text-sm font-medium text-foreground">
          Comments on the dictations
        </h4>
        <ul className="mt-2 flex flex-col gap-2">
          {answers.map((x) => {
            const notes = x.reviews.filter((r) => r.comments);
            return (
              <li key={x.assignment_case_id} className="text-sm">
                <span className="font-medium text-foreground">
                  {x.position + 1}. {x.title}
                </span>
                {x.has_audio ? (
                  x.duration_seconds != null && (
                    <span className="ml-2 text-xs tabular-nums text-foreground-muted">
                      {Math.floor(x.duration_seconds / 60)}:
                      {String(x.duration_seconds % 60).padStart(2, "0")}
                    </span>
                  )
                ) : (
                  <span className="ml-2 text-xs uppercase text-status-urgent">
                    no recording
                  </span>
                )}
                {notes.length === 0 ? (
                  <p className="text-xs italic text-foreground-muted">
                    No comment recorded.
                  </p>
                ) : (
                  notes.map((r) => (
                    <p key={r.id} className="text-xs text-foreground">
                      <span className="text-foreground-muted">
                        {r.reviewer_name ?? "Reviewer"}:
                      </span>{" "}
                      {r.comments}
                    </p>
                  ))
                )}
              </li>
            );
          })}
        </ul>
      </div>

    </section>
  );
}
